// Low-poly tree meshes, spawning, and the multi-hit chopping mechanic.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.

import * as THREE from 'three';
import { HEX_H, PROP_SCALE, hexToWorld } from '@world/terrain';
import { DAY_LEN } from '@world/weather';
import { toonMat, addOutlines } from '@world/toon';

// Two size tiers, split by the same `v` variety value makeTreeMesh already
// uses to pick a shape: the no-trunk, low-blob shape (0.72-0.88) reads as
// a bush next to the other four (all of which have a proper trunk), so it
// gets the smaller yield/faster chop instead of a new mesh category.
export const TREE_CHOP_HITS = 3;
export const BUSH_CHOP_HITS = 1;
export const TREE_WOOD = 3;
export const BUSH_WOOD = 1;
const BUSH_RESPAWN_SECONDS = DAY_LEN * 7; // a week of in-game time
export const CHOP_RANGE = 2.3;

export const treeGroup = new THREE.Group();
// Stumps render separately from treeGroup so a felled tree's stump never
// intercepts the chop raycast (main.ts only casts against treeGroup) —
// otherwise a stump could silently block a click meant for the water or
// fish just behind it.
export const stumpGroup = new THREE.Group();

interface TreeSpawn { q: number; r: number; h: number; v: number; kind: 'tree' | 'bush' }

const trees = new Map<string, THREE.Group>();
const stumps = new Map<string, THREE.Group>();
const treeSpawns = new Map<string, TreeSpawn>(); // kept even after felling, so bushes can respawn identically
const choppedTrees = new Set<string>();
const treeHits = new Map<string, number>();
const shakingTrees = new Map<string, number>();
const felledAt = new Map<string, number>(); // treeKey -> gameTime felled; bushes only (respawn timer)

const trunkGeo = new THREE.CylinderGeometry(0.09, 0.15, 1, 5);
const blobGeo = new THREE.IcosahedronGeometry(1, 0);
const coneGeo = new THREE.ConeGeometry(1, 1, 6);
const culmGeo = new THREE.CylinderGeometry(0.024, 0.032, 1, 5);
const trunkMat = toonMat(0x6b5540);
const culmMat = toonMat(0x93a86a);

function leafMatFor(c: number): THREE.MeshToonMaterial {
  return toonMat(c);
}
// Species-flavoured foliage palettes. Each shape bucket in makeTreeMesh
// just draws from a different one of these via the same v3-hash-into-array
// trick it always used — so the extra variety is free: same shared
// geometries, same draw-call count, only more Material instances.
// Desaturated against the watercolour terrain: full-strength greens read as
// plastic next to a wash.
const leafPalette = [0x6f9455, 0x5f8449, 0x7fa363, 0x54763f].map(leafMatFor); // broadleaf forest green
const maplePalette = [0xc26a3a, 0xb05442, 0xd08c4e, 0xd9b45c].map(leafMatFor); // momiji autumn
const pinePalette = [0x53785a, 0x486a50, 0x3f5d47].map(leafMatFor); // muted matsu green
const bambooLeafPalette = [0xa3bb6f, 0xb4c882, 0x94ac63].map(leafMatFor);
const blossomPalette = [0xecc3d3, 0xe3b0c4, 0xf2d7e2].map(leafMatFor); // sakura
const bushPalette = [...leafPalette, ...blossomPalette]; // green shrub, or a flowering azalea
// Mostly forest canopy, occasionally a flowering sakura or a golden ginkgo
// (reusing maplePalette's gold material rather than allocating a new one).
const bigTreePalette = [...leafPalette, ...leafPalette, blossomPalette[0], maplePalette[3]];

function pickMat(palette: THREE.Material[], t: number): THREE.Material {
  return palette[Math.floor(t * palette.length) % palette.length];
}

function treeKeyFor(q: number, r: number): string {
  return 'T' + q + ',' + r;
}

function makeTreeMesh(v: number): THREE.Group {
  const g = new THREE.Group();
  const v2 = (v * 7919) % 1, v3 = (v * 104729) % 1;
  const s = 0.9 + v2 * 0.5;
  let leafMat: THREE.Material = pickMat(leafPalette, v3);

  function trunk(h: number, thick: number): number {
    const m = new THREE.Mesh(trunkGeo, trunkMat);
    m.scale.set(thick, h, thick);
    m.position.y = h / 2;
    g.add(m);
    return h;
  }
  function blob(radius: number, y: number, dx?: number, dz?: number, flat = 0.85): void {
    const m = new THREE.Mesh(blobGeo, leafMat);
    m.scale.set(radius, radius * flat, radius);
    m.position.set(dx || 0, y, dz || 0);
    m.rotation.y = v3 * Math.PI;
    g.add(m);
  }

  if (v < 0.3) {
    if (v3 < 0.4) {
      // Ume (Japanese apricot): near-bare, angled branches with small
      // blossom clusters at the tips instead of a full leaf canopy.
      leafMat = pickMat(blossomPalette, v3);
      const th = trunk(1.05 * s, 0.75);
      [-0.55, 0.1, 0.65].forEach((a, i) => {
        const bl = 0.62 * s * (0.85 + i * 0.12);
        const dirX = Math.sin(a), dirY = Math.cos(a);
        const branch = new THREE.Mesh(trunkGeo, trunkMat);
        branch.scale.set(0.5, bl, 0.5);
        branch.position.set(dirX * bl / 2, th + dirY * bl / 2, (i - 1) * 0.08 * s);
        branch.rotation.z = a;
        g.add(branch);
        blob(0.22 * s, th + dirY * bl, dirX * bl, (i - 1) * 0.08 * s, 0.7);
      });
    } else {
      // Momiji-style maple: rounded broadleaf silhouette, autumn palette
      leafMat = pickMat(maplePalette, v3);
      const th = trunk(0.9 * s, 1);
      blob(0.52 * s, th + 0.3 * s);
      blob(0.34 * s, th + 0.12 * s, 0.3 * s, 0.22 * s);
    }
  } else if (v < 0.52) {
    leafMat = pickMat(pinePalette, v3);
    if (v3 < 0.34) {
      // Niwaki-style pine: a trunk topped by flattened, offset "cloud"
      // pads — reads as a pruned garden pine (matsu) rather than a fir.
      const th = trunk(1.55 * s, 0.85);
      blob(0.58 * s, th * 0.48, -0.24 * s, 0.1 * s, 0.32);
      blob(0.68 * s, th * 0.76, 0.28 * s, -0.06 * s, 0.32);
      blob(0.48 * s, th * 1.0, 0.06 * s, 0.24 * s, 0.32);
    } else if (v3 < 0.67) {
      // Japanese black pine: the whole tree leaned over, with a wide, low,
      // asymmetric spread — windswept/coastal rather than tidy.
      g.rotation.z = (v3 - 0.5) * 0.5;
      const th = trunk(1.3 * s, 0.9);
      blob(0.7 * s, th * 0.52, -0.38 * s, 0.16 * s, 0.28);
      blob(0.56 * s, th * 0.82, 0.44 * s, -0.11 * s, 0.28);
      blob(0.42 * s, th * 0.65, 0.06 * s, -0.36 * s, 0.28);
    } else {
      // Japanese cedar (sugi): a dense, tall pyramidal conifer stack
      const th = trunk(1.6 * s, 0.7);
      [[0.5, 0.85, 0.42], [0.38, 0.7, 0.68], [0.24, 0.55, 0.9]].forEach(([r, h, yf]) => {
        const cone = new THREE.Mesh(coneGeo, leafMat);
        cone.scale.set(r * s, h * s, r * s);
        cone.position.y = th * yf;
        g.add(cone);
      });
    }
  } else if (v < 0.72) {
    // Bamboo: a broad grove of slender culms spread across most of the hex,
    // each carrying leaf tufts up its length. These offsets are in the
    // group's local space, which PROP_SCALE later divides by three, so
    // covering a hex takes local radii around 1.3 — not the ~0.1 that read
    // as a tight clump before.
    leafMat = pickMat(bambooLeafPalette, v3);
    const CULMS = 7;
    for (let i = 0; i < CULMS; i++) {
      const j = (v3 * 97 + i * 37.13) % 1;
      const k = (v2 * 53 + i * 19.7) % 1;
      const a = (i / CULMS) * Math.PI * 2 + v3 * 6.3;
      const rad = 0.3 + j * 1.0;
      const ox = Math.cos(a) * rad, oz = Math.sin(a) * rad;
      const h = 1.7 * s * (0.7 + k * 0.65);
      const culm = new THREE.Mesh(culmGeo, culmMat);
      culm.scale.set(1.6, h, 1.6);
      culm.position.set(ox, h / 2, oz);
      culm.rotation.z = (j - 0.5) * 0.18;
      culm.rotation.x = (k - 0.5) * 0.18;
      g.add(culm);
      const tuft = new THREE.Mesh(coneGeo, leafMat);
      tuft.scale.set(0.3 * s, 0.52 * s, 0.3 * s);
      tuft.position.set(ox + (j - 0.5) * 0.14, h + 0.12 * s, oz);
      g.add(tuft);
      const tuft2 = new THREE.Mesh(coneGeo, leafMat);
      tuft2.scale.set(0.22 * s, 0.4 * s, 0.22 * s);
      tuft2.position.set(ox - 0.09, h - 0.3 * s, oz + 0.09);
      g.add(tuft2);
    }
  } else if (v < 0.88) {
    // Low shrub: usually green groundcover, sometimes a flowering azalea
    leafMat = pickMat(bushPalette, v3);
    blob(0.32 * s, 0.2 * s);
    blob(0.2 * s, 0.16 * s, 0.24 * s, 0.1 * s);
  } else {
    // Big canopy tree: mostly forest green, occasionally a flowering sakura
    leafMat = pickMat(bigTreePalette, v3);
    const th = trunk(1.35 * s, 1.9);
    blob(0.62 * s, th + 0.3 * s);
    blob(0.42 * s, th + 0.05 * s, 0.42 * s, 0.2 * s);
    blob(0.4 * s, th + 0.1 * s, -0.35 * s, -0.3 * s);
  }
  g.rotation.y = v2 * Math.PI * 2;
  g.scale.setScalar(PROP_SCALE);
  addOutlines(g);
  return g;
}

function kindFor(v: number): 'tree' | 'bush' {
  return v >= 0.72 && v < 0.88 ? 'bush' : 'tree';
}

// Mirrors makeTreeMesh's own v-range branching so a stump reads as "the
// stub of whatever was just standing here": a single cut trunk for the
// broad-leaf/conifer/big-tree shapes, several short cut culms for bamboo
// (which never had one central trunk to begin with), and a low cropped
// leaf mound for bushes (which had no trunk at all).
function makeStumpMesh(v: number): THREE.Group {
  const g = new THREE.Group();
  const s = 0.9 + ((v * 7919) % 1) * 0.5; // same size formula makeTreeMesh uses, so the stump matches its felled tree
  const v3 = (v * 104729) % 1;
  const leafMat = pickMat(bushPalette, v3); // only used by the shrub/azalea stump below; matches its foliage pick

  if (v >= 0.52 && v < 0.72) {
    // Cut culms left across the grove's whole footprint — mirrors the ring
    // makeTreeMesh lays down, so the stumps land where the bamboo stood.
    for (let i = 0; i < 7; i++) {
      const j = (v3 * 97 + i * 37.13) % 1;
      const a = (i / 7) * Math.PI * 2 + v3 * 6.3;
      const rad = 0.3 + j * 1.0;
      const stub = new THREE.Mesh(culmGeo, culmMat);
      stub.scale.set(1.6, 0.12 * s, 1.6);
      stub.position.set(Math.cos(a) * rad, 0.06 * s, Math.sin(a) * rad);
      g.add(stub);
    }
  } else if (v >= 0.72 && v < 0.88) {
    const m = new THREE.Mesh(blobGeo, leafMat);
    m.scale.set(0.3 * s, 0.1 * s, 0.3 * s);
    m.position.y = 0.05 * s;
    g.add(m);
  } else if (v >= 0.88) {
    const m = new THREE.Mesh(trunkGeo, trunkMat);
    m.scale.set(0.85 * s, 0.2, 0.85 * s); // the big tree's trunk reads much thicker, so its stump is wider too
    m.position.y = 0.1;
    g.add(m);
  } else {
    const m = new THREE.Mesh(trunkGeo, trunkMat);
    m.scale.set(0.55 * s, 0.16, 0.55 * s);
    m.position.y = 0.08;
    g.add(m);
  }
  g.scale.setScalar(PROP_SCALE);
  addOutlines(g);
  return g;
}

// Creates and registers a tree's mesh for hex (q, r) at terrain height h,
// with v (0..1) picking its variety/shape/size. Returns the tree's key so
// the caller (chunk streaming) can track it for later removal on unload,
// or null if that hex's tree has already been chopped down (a bush's
// stump still counts as "chopped" until its respawn timer elapses).
export function spawnTree(q: number, r: number, h: number, v: number): string | null {
  const tk = treeKeyFor(q, r);
  treeSpawns.set(tk, { q, r, h, v, kind: kindFor(v) });
  if (choppedTrees.has(tk)) return null;
  if (trees.has(tk)) return tk;
  const g = makeTreeMesh(v);
  const pos = hexToWorld(q, r);
  g.position.set(pos.x, h * HEX_H + HEX_H / 2, pos.z);
  g.userData.baseRotY = g.rotation.y;
  g.traverse((o) => { o.userData.treeKey = tk; });
  treeGroup.add(g);
  trees.set(tk, g);
  return tk;
}

export function removeTreeMesh(tk: string): void {
  const g = trees.get(tk);
  if (g) { treeGroup.remove(g); trees.delete(tk); }
}

export function getTreeMesh(tk: string): THREE.Group | undefined {
  return trees.get(tk);
}

// Registers one axe hit on a tree/bush; once its size tier's hit count is
// reached it's felled (mesh swapped for a stump). `now` is the current
// gameTime — recorded only for bushes, which regrow after a week; big
// trees stay stumps permanently. Callers handle the wood reward/toast.
export function chopTree(tk: string, now: number): { felled: boolean; hits: number; requiredHits: number; wood: number } {
  const spawn = treeSpawns.get(tk);
  const kind = spawn?.kind ?? 'tree';
  const requiredHits = kind === 'bush' ? BUSH_CHOP_HITS : TREE_CHOP_HITS;
  const hits = (treeHits.get(tk) || 0) + 1;
  if (hits >= requiredHits) {
    treeHits.delete(tk);
    shakingTrees.delete(tk);
    removeTreeMesh(tk);
    choppedTrees.add(tk);
    if (spawn) {
      const stump = makeStumpMesh(spawn.v);
      const pos = hexToWorld(spawn.q, spawn.r);
      stump.position.set(pos.x, spawn.h * HEX_H + HEX_H / 2, pos.z);
      stumpGroup.add(stump);
      stumps.set(tk, stump);
      if (kind === 'bush') felledAt.set(tk, now);
    }
    return { felled: true, hits, requiredHits, wood: kind === 'bush' ? BUSH_WOOD : TREE_WOOD };
  }
  treeHits.set(tk, hits);
  shakingTrees.set(tk, 0.45);
  return { felled: false, hits, requiredHits, wood: 0 };
}

// Regrows any felled bush whose week-long respawn timer has elapsed —
// big trees never respawn, so their stumps are permanent. Call this
// periodically (main.ts's throttled market-timer tick is fine; this
// doesn't need per-frame precision).
export function updateTreeRespawns(now: number): void {
  felledAt.forEach((t, tk) => {
    if (now - t < BUSH_RESPAWN_SECONDS) return;
    felledAt.delete(tk);
    choppedTrees.delete(tk);
    const stump = stumps.get(tk);
    if (stump) { stumpGroup.remove(stump); stumps.delete(tk); }
    const spawn = treeSpawns.get(tk);
    if (spawn) spawnTree(spawn.q, spawn.r, spawn.h, spawn.v);
  });
}

export function updateTreeShake(dt: number): void {
  shakingTrees.forEach((time, tk) => {
    const g = trees.get(tk);
    if (!g) { shakingTrees.delete(tk); return; }
    const t2 = time - dt;
    if (t2 <= 0) {
      g.rotation.z = 0;
      shakingTrees.delete(tk);
    } else {
      g.rotation.z = Math.sin(t2 * 55) * 0.05 * (t2 / 0.45);
      shakingTrees.set(tk, t2);
    }
  });
}
